import { NotificationProps } from "./interfaces/props";
import { InputData } from "./interfaces/stores";

// Función para validar y sanitizar los límites horarios
const validateHourLimits = (minHour: number, maxHour: number) => {
  // Validar que sean números válidos
  const validMinHour =
    !isNaN(minHour) && minHour >= 0 && minHour <= 23 ? minHour : 4;
  const validMaxHour =
    !isNaN(maxHour) && maxHour >= 0 && maxHour <= 23 ? maxHour : 23;

  // Asegurar que MIN_HOUR < MAX_HOUR
  if (validMinHour >= validMaxHour) {
    console.warn(
      `Configuración inválida: MIN_HOUR (${validMinHour}) debe ser menor que MAX_HOUR (${validMaxHour}). Usando valores por defecto.`
    );
    return { MIN_HOUR: 4, MAX_HOUR: 23 };
  }

  return { MIN_HOUR: validMinHour, MAX_HOUR: validMaxHour };
};

// Configuración de límites horarios (configurable desde .env)
export const HOUR_LIMITS = (() => {
  const minHour = parseInt(process.env.REACT_APP_MIN_HOUR || "4");
  const maxHour = parseInt(process.env.REACT_APP_MAX_HOUR || "23");

  return validateHourLimits(minHour, maxHour);
})();

// Función auxiliar para obtener información de configuración
export const getHourLimitsInfo = () => {
  return {
    minHour: HOUR_LIMITS.MIN_HOUR,
    maxHour: HOUR_LIMITS.MAX_HOUR,
    minHourText: `${HOUR_LIMITS.MIN_HOUR.toString().padStart(2, "0")}:00`,
    maxHourText: `${HOUR_LIMITS.MAX_HOUR.toString().padStart(2, "0")}:00`,
    totalHours: HOUR_LIMITS.MAX_HOUR - HOUR_LIMITS.MIN_HOUR + 1,
    isConfigured:
      process.env.REACT_APP_MIN_HOUR !== undefined &&
      process.env.REACT_APP_MAX_HOUR !== undefined,
  };
};

export const doctorStatuses = {
  APPROVAL_PENDING: "APPROVAL_PENDING",
  ENABLED: "ENABLED",
  REJECTED: "REJECTED",
  SUSPENDED: "SUSPENDED",
};

export enum UserTypes {
  patient = "PATIENT",
  doctor = "DOCTOR",
  admin = "ADMIN",
  superAdmin = "SUPERADMIN",
  secretary = "SECRETARY",
}

export enum AvailabilitySlotType {
  videocall = "VIDEOCALL",
  presential = "PRESENTIAL",
  presentialVideocall = "PRESENTIAL_VIDEOCALL",
}

export enum AppointmentSlotType {
  videocall = "VIDEOCALL",
  presential = "PRESENTIAL",
}

/** NOTIFICATIONS */

export interface NotificationsStoreProps extends NotificationProps {
  contentType: string;
  patientId: number;
  doctorId: number;
  adminId: number;
  availabilitySlotId: number;
  appointmentId: number;
  rapportNoteId: number;
}

/** APPOINTMENT */

type Specialty = {
  id: number;
  title: string;
  tag: string;
};

type HealthCoverage = {
  id: number;
  title: string;
  tag: string;
};

enum AppointmentStatus {
  BOOKED = "BOOKED",
  CANCELLED_DOCTOR = "CANCELLED_DOCTOR",
  CANCELLED_PATIENT_REFUND = "CANCELLED_PATIENT_REFUND",
  CANCELLED_PATIENT_NO_REFUND = "CANCELLED_PATIENT_NO_REFUND",
  CONFIRMED = "CONFIRMED",
}

type Patient = {
  firstName: string;
  lastName: string;
};

export type Appointment = {
  id: number;
  startDate: string;
  startText: string;
  specialty: Specialty;
  healthCoverage: HealthCoverage;
  patient: Patient;
  appointmentFee: number;
  appointmentType: AppointmentSlotType;
  appointmentStatus: AppointmentStatus;
  cancelledAt?: string;
};

/** RAPPORTS */
export interface RapportProps {
  id: number;
  lastName: string;
  firstName: string;
  email: string;
  profilePictureUrl?: string;
}

export interface RapportResponse {
  id: number;
  patient: {
    id: string;
    lastName: string;
    firstName: string;
    email: string;
    profilePictureUrl?: string;
  };
}

export interface HistoryProsp {
  appointmentId: number;
  appointmentType: string;
  appointmentDate: string;
  appointmentDateText: string;
  specialty: {
    id: number;
    tag: string;
    title: string;
  };
}

export interface NoteProps {
  id: number;
  rapportId: number;
  note: string;
  createdAt: string;
  updatedAt: string;
}
export interface medicalRecordsDetailProps {
  description: string;
  date: string;
}

export interface MedicalRecordsProps {
  id: number;
  recordType: string;
  detail: medicalRecordsDetailProps;
}

export interface RapportDetails {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  notes: NoteProps[];
  history: HistoryProsp[];
  medicalRecords: MedicalRecordsProps[];
}

export const medicalRecordsArray = [
  {
    id: 1,
    recordType: "Medicacion",
    detail: {
      description: "Levotiroxina marca GlaxoSmithKline (es la de color rosa)",
      date: "29/03/23",
    },
  },
  {
    id: 2,
    recordType: "Patologia",
    detail: {
      description: "Levotiroxina marca GlaxoSmithKline (es la de color rosa)",
      date: "30/03/23",
    },
  },
  {
    id: 3,
    recordType: "Procedimiento quirurgico",
    detail: {
      description: "Levotiroxina marca GlaxoSmithKline (es la de color rosa)",
      date: "31/03/23",
    },
  },
];
export interface DoctorProfileData {
  firstName?: string;
  lastName?: string;
  phone?: string;
}

export interface MedicalProfileData {
  healthCoveragesIds: number[];
  healthCoverages: HealthCoverage[];
  specialtiesIds: number[];
  trajectory: string;
}

export interface doctorFeeData {
  presentialFee: number;
  videocallFee: number;
}

export interface AvailabilityData {
  presentialAppointments: boolean;
  homeAppointments: boolean;
  doctorAddress?: {
    street: string;
    streetNumber: number | string;
    floorApartment: string;
    province: InputData;
    city: InputData;
  };
  videocallAppointments: boolean;
}

export interface AvailabilityInputData {
  presentialAppointments: boolean;
  homeAppointments: boolean;
  doctorAddress: {
    street: number;
    streetNumber: number;
    floorApartment: string;
    provinceId: number;
    cityId: number;
  };
  videocallAppointments: boolean;
}

export interface DoctorData {
  id: number;
  licence: number;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  doctorStatus: string;
  isActive: boolean;
  userId: number;
}

export interface SecretaryData {
  id: number;
  userId: number;
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  isActive: boolean;
}

export interface PasswordFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
}

export type AddAvailabilityModalInitValueTypes = {
  remote: boolean;
  onSite: boolean;
  date: Date;
  startHour: string;
  endHour: string;
  selectedDays: string[];
  validity: string;
  duration: string;
};

export interface ReportsItem {
  id: number;
  firstName: string;
  lastName: string;
  profilePictureUrl: string;
  email: string;
}

export interface DoctorProfile {
  id: number;
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  type: string;
  emailVerified: boolean;
  phone: string;
  licence: number;
  videocallFee: number;
  presentialFee: number;
  presentialAppointments: boolean;
  homeAppointments: boolean;
  videocallAppointments: boolean;
  doctorStatus: string;
  trajectory: string;
  profilePictureUrl: string;
  isActive: boolean;
  hasMercadoPago: boolean;
  mercadoPagoUserName: string;
  mercadoPagoEmail: string;
  healthCoverages: [
    {
      id: 0;
      title: string;
      tag: string;
    }
  ];
  specialties: [
    {
      id: 0;
      title: string;
      tag: string;
    }
  ];
  address?: {
    id: number;
    street: string;
    streetNumber: 0;
    floorApartment: string;
    province: {
      id: number;
      title: string;
      tag: string;
    };
    city: {
      id: number;
      title: string;
      tag: string;
    };
  };
}

export interface NewAvailabilityDto {
  startDate: Date;
  endDate: Date;
  availabilitySlotType: AvailabilitySlotType;
  rrule?: string;
  duration?: number;
}

export interface EditAvailabilityDto {
  availabilitySlotsIds: Array<string | number | undefined>;
  availabilitySlotType: AvailabilitySlotType;
}

export const APPOINTMENT_STATE_LABELS = {
  RESERVADO: "Reservado",
  CANCELADO: "Cancelado",
  REALIZADO: "Realizado",
};

//REPORTS

export const modalidades = [
  { id: 0, title: "Videollamada" },
  { id: 1, title: "Presencial" },
];

export const coberturas = [
  { id: "0", title: "Sin cobertura" },
  { id: "1", title: "Con cobertura" },
];

export const estados = [
  { id: 0, title: "Realizado" },
  { id: 1, title: "Reservado" },
  { id: 2, title: "Cancelado" },
];

export interface Doctor {
  id: number;
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  type: "DOCTOR";
  emailVerified: boolean;
  phone: string;
  licence: number;
  videocallFee: number;
  presentialFee: number;
  presentialAppointments: boolean;
  homeAppointments: boolean;
  videocallAppointments: boolean;
  doctorStatus: "APPROVAL_PENDING" | "APPROVED" | "REJECTED";
  trajectory: string;
  profilePictureUrl: string;
  isActive: boolean;
  hasMercadoPago: boolean;
  mercadoPagoUserName: string;
  mercadoPagoEmail: string;
}

export interface CreateAppointmentData {
  availabilitySlotId: number;
  specialtyId: number;
  healthCoverageId?: number;
  appointmentType: string;
  timezone: string;
  patientId: number;
}

// ===== PATIENT MODAL INTERFACES - MEDAPP-5 =====

/**
 * Datos del formulario de creación de paciente
 * Coincide exactamente con los campos del modal de las imágenes
 */
export interface CreatePatientFormData {
  firstName: string; // "Nombre/s" field
  lastName: string; // "Apellidos" field
  email: string; // "Correo electrónico" field
}

/**
 * Props del modal principal de creación de paciente
 */
export interface CreatePatientModalProps {
  open: boolean;
  onClose: () => void;
  onPatientCreated?: (patient: CreatePatientFormData) => void;
  onSubmit?: (data: CreatePatientFormData) => Promise<void>;
}

/**
 * Props del modal de éxito/confirmación
 */
export interface PatientSuccessModalProps {
  open: boolean;
  onClose: () => void;
  patientName?: string;
  patientEmail?: string;
  onCreateAnother?: () => void;
}

/**
 * Estados del formulario para manejo de UI
 */
export interface CreatePatientFormState {
  isSubmitting: boolean;
  hasErrors: boolean;
  submitError: string | null;
  successMessage: string | null;
}

/**
 * Mensajes de validación específicos para el modal
 */
export interface PatientValidationMessages {
  firstName: {
    required: string;
    pattern: string;
    minLength: string;
    maxLength: string;
  };
  lastName: {
    required: string;
    pattern: string;
    minLength: string;
    maxLength: string;
  };
  email: {
    required: string;
    format: string;
    maxLength: string;
    unique?: string;
  };
}

/**
 * Configuración del modal según las imágenes
 */
export interface ModalConfiguration {
  title: string;
  width: number;
  closeOnBackdropClick: boolean;
  showCloseButton: boolean;
}

/**
 * Estilos específicos para el modal (según imágenes)
 */
export interface ModalStyles {
  primaryButtonColor: string; // Color amarillo/verde del botón "Crear paciente"
  secondaryButtonColor: string; // Color del botón "Cancelar"
  errorColor: string; // Color rojo de mensajes de error
  successColor: string; // Color verde del checkmark
  overlayColor: string; // Color del overlay del modal
}

// ===== PATIENT VALIDATION CONSTANTS =====

/**
 * Patrones de validación para campos del paciente
 * Basados en las validaciones existentes de MedApp
 */
export const PATIENT_VALIDATION_PATTERNS = {
  // Solo letras, espacios, acentos y ñ, entre 1 y 25 caracteres
  NAME_PATTERN: /^[a-zA-Z áéíóúñÁÉÍÓÚÑ]{1,25}$/,

  // Email format básico
  EMAIL_PATTERN: /^\S+@\S+\.\S+$/,
} as const;

/**
 * Límites de caracteres para campos
 */
export const PATIENT_FIELD_LIMITS = {
  FIRST_NAME_MIN: 1,
  FIRST_NAME_MAX: 25,
  LAST_NAME_MIN: 1,
  LAST_NAME_MAX: 25,
  EMAIL_MAX: 60,
} as const;

/**
 * Mensajes de validación en español (según imágenes)
 */
export const PATIENT_VALIDATION_MESSAGES: PatientValidationMessages = {
  firstName: {
    required: "Es obligatorio ingresar el nombre",
    pattern: "El nombre debe tener solo letras y entre 1 a 25 caracteres",
    minLength: "El nombre debe tener al menos 1 carácter",
    maxLength: "El nombre no puede tener más de 25 caracteres",
  },
  lastName: {
    required: "Es obligatorio ingresar el apellido",
    pattern: "El apellido debe tener solo letras y entre 1 a 25 caracteres",
    minLength: "El apellido debe tener al menos 1 carácter",
    maxLength: "El apellido no puede tener más de 25 caracteres",
  },
  email: {
    required: "Es obligatorio ingresar el email",
    format: "El email no tiene un formato válido",
    maxLength: "El email es demasiado largo",
    unique: "Este email ya está registrado en el sistema",
  },
};

/**
 * Placeholders para los campos (exactos de las imágenes)
 */
export const PATIENT_FIELD_PLACEHOLDERS = {
  FIRST_NAME: "Ingresar nombre/s",
  LAST_NAME: "Ingresar apellidos",
  EMAIL: "Ingresar correo electrónico",
} as const;

/**
 * Textos del modal (basados en imágenes)
 */
export const PATIENT_MODAL_TEXTS = {
  // Modal principal
  CREATE_TITLE: "Nuevo paciente",
  CANCEL_BUTTON: "Cancelar",
  SUBMIT_BUTTON: "Crear paciente",

  // Modal de éxito
  SUCCESS_TITLE: "Nuevo paciente creado con éxito",
  SUCCESS_MESSAGE:
    "Se ha enviado un email con las credenciales de acceso al paciente.",
  SUCCESS_BUTTON: "Aceptar",
  CREATE_ANOTHER_BUTTON: "Crear otro paciente",

  // Estados de carga
  SUBMITTING_TEXT: "Creando paciente...",

  // Errores generales
  GENERIC_ERROR: "Ha ocurrido un error. Por favor, intente nuevamente.",
  NETWORK_ERROR: "Error de conexión. Verifique su internet.",
} as const;

/**
 * Configuración del modal según las imágenes
 */
export const PATIENT_MODAL_CONFIG: ModalConfiguration = {
  title: PATIENT_MODAL_TEXTS.CREATE_TITLE,
  width: 500, // pixels aprox según imágenes
  closeOnBackdropClick: true,
  showCloseButton: true,
};

/**
 * Colores del modal (estimados de las imágenes)
 */
export const PATIENT_MODAL_STYLES: ModalStyles = {
  primaryButtonColor: "#DAE86D", // Amarillo/verde del botón principal
  secondaryButtonColor: "#E0E0E0", // Gris del botón cancelar
  errorColor: "#FF0000", // Rojo de errores
  successColor: "#4CAF50", // Verde del checkmark
  overlayColor: "rgba(0, 0, 0, 0.5)", // Overlay del modal
};

// ===== UTILITY TYPES =====

/**
 * Tipo para los nombres de campos del formulario
 */
export type PatientFormField = keyof CreatePatientFormData;

/**
 * Tipo para los estados posibles del modal
 */
export type PatientModalState = "idle" | "submitting" | "success" | "error";

/**
 * Tipo para las acciones del formulario
 */
export type PatientFormAction =
  | { type: "SUBMIT_START" }
  | { type: "SUBMIT_SUCCESS"; payload: CreatePatientFormData }
  | { type: "SUBMIT_ERROR"; payload: string }
  | { type: "RESET_FORM" }
  | { type: "CLOSE_MODAL" };

/**
 * Tipo para validación de campo individual
 */
export interface FieldValidationResult {
  isValid: boolean;
  message?: string;
}

/**
 * Tipo para validación completa del formulario
 */
export interface FormValidationResult {
  isValid: boolean;
  errors: Partial<Record<PatientFormField, string>>;
}

export const SPECIAL_SLOT_HEALTH_COVERAGES = ['hospital_bernardo_houssay', 'clinica_de_anticoagulacion_higa_dr_oscar_alende']